import Reveal from '@/components/base/Reveal';
import { heroEntries, heroStats } from '@/mocks/home';

export default function Hero() {
  return (
    <section id="top" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-[1400px] px-4 pb-12 pt-8 md:px-8 md:pb-16 md:pt-12">
        <Reveal>
          <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100/70 p-2 sm:flex-row sm:items-center">
            <span className="hidden shrink-0 items-center gap-1.5 rounded-md bg-foreground-950 px-3 py-2 text-xs font-medium text-background-100 sm:flex">
              <i className="ri-compass-3-line text-sm text-accent-400" />
              追踪入口
            </span>
            <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-1 sm:items-center">
              {heroEntries.map((entry) => (
                <a
                  key={entry.label}
                  href={entry.href}
                  className="group flex flex-1 items-center gap-2.5 rounded-md border border-transparent bg-background-50 px-3 py-2.5 transition-colors hover:border-background-300"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className={`${entry.icon} text-base`} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground-900">{entry.label}</span>
                    <span className="block truncate text-xs text-foreground-500">{entry.desc}</span>
                  </span>
                  <i className="ri-arrow-right-s-line ml-auto hidden text-base text-foreground-400 transition-transform group-hover:translate-x-0.5 sm:block" />
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-14">
          <Reveal className="lg:col-span-2">
            <p className="flex items-center gap-2 text-sm font-medium text-primary-600">
              <span className="h-px w-8 bg-primary-500" />
              深挖 · 解析 · 还原
            </p>
            <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 text-balance md:text-5xl">
              把表面之下的细节，
              <br className="hidden sm:block" />
              一条一条摊开来看
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground-600 md:text-lg">
              黑料一区专注深挖与解析，提供详细扒皮、真相还原、内幕分析等深度内容，帮你理清事件脉络，看到表面之下的细节与逻辑。
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <a
                href="#featured"
                className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-flashlight-line text-base" />
                开始深挖
              </a>
              <a
                href="#collections"
                className="flex h-11 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 text-sm font-medium text-foreground-800 transition-colors hover:border-foreground-400"
              >
                <i className="ri-stack-line text-base" />
                浏览合集索引
              </a>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="h-full rounded-lg border border-background-200 bg-background-100/60 p-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground-900">当前追踪</span>
                <span className="flex items-center gap-1.5 text-xs text-primary-600">
                  <span className="block h-1.5 w-1.5 rounded-full bg-primary-500 animate-hly-pulse" />
                  实时
                </span>
              </div>

              <ul className="mt-4 space-y-3">
                <li className="flex items-start gap-3 rounded-md border border-background-200 bg-background-50 p-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-accent-100 font-mono text-[11px] font-semibold text-accent-900">
                    01
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-900">综艺剪辑争议</span>
                    <span className="block text-xs text-foreground-500">追踪中 · 第 9 天 · 5 个节点</span>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-md p-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-secondary-100 font-mono text-[11px] font-semibold text-secondary-800">
                    02
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-900">代言反噬曲线</span>
                    <span className="block text-xs text-foreground-500">追踪中 · 第 6 天 · 4 个节点</span>
                  </span>
                </li>
                <li className="flex items-start gap-3 rounded-md p-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-secondary-100 font-mono text-[11px] font-semibold text-secondary-800">
                    03
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground-900">搬运链条调查</span>
                    <span className="block text-xs text-foreground-500">追踪中 · 第 4 天 · 3 个节点</span>
                  </span>
                </li>
              </ul>

              <a
                href="#timeline"
                className="mt-4 flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                查看完整脉络
                <i className="ri-arrow-right-s-line text-sm" />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={80}>
          <dl className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-background-200 pt-8 md:grid-cols-4">
            {heroStats.map((stat) => (
              <div key={stat.label} className="flex flex-col gap-1">
                <dt className="order-2 text-sm text-foreground-500">{stat.label}</dt>
                <dd className="order-1 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {stat.value}
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>
      </div>
    </section>
  );
}